import { CryoDTO, SoakingDTO } from 'business/models/Models';
import { IOption } from 'components/input/selectInput';
import { InputHTMLAttributes } from 'react';
import { ProtectantAgentType } from './constants';
import { ModalDetails } from 'business/models/CustomModels';
import CryoprotectantService from 'business/services/CryoprotectantService';
import SoakingService from 'business/services/SoakingService';

const ConcUnitOptions: IOption[] = [
  { value: '', title: '' },
  { value: '%', title: '%' },
  { value: '%v/v', title: '%v/v' },
  { value: '%w/v', title: '%w/v' },
  { value: 'mM', title: 'mM' },
  { value: 'M', title: 'M' },
];

const TimeUnitOptions: IOption[] = [
  { value: '', title: '' },
  { value: 'M', title: 'Min' },
  { value: 'H', title: 'Hour' },
  { value: 'D', title: 'Day' },
];

interface EditOptionsType extends InputHTMLAttributes<HTMLInputElement> {
  inputType: React.HTMLInputTypeAttribute;
  options?: IOption[];
}

export const cryoprotectantColumns: {
  accessor: string;
  Header: string;
  className?: string;
  editOptions?: EditOptionsType;
}[] = [
  {
    accessor: 'ingredient',
    Header: 'Ingredient',
    editOptions: { inputType: 'text', required: true },
  },
  {
    accessor: 'conc',
    Header: 'Conc.',
    className: 'w-2 max-w-[50px]',
    editOptions: {
      inputType: 'number',
      min: 0,
      required: true,
    },
  },
  {
    accessor: 'concUnit',
    Header: 'Unit',
    className: 'w-3 max-w-[70px]',
    editOptions: {
      inputType: 'select',
      options: ConcUnitOptions,
      required: true,
    },
  },
  {
    accessor: 'pH',
    Header: 'pH',
    className: 'w-2 max-w-[50px]',
    editOptions: {
      inputType: 'number',
      min: 0,
      step: 0.1,
    },
  },
  {
    accessor: 'protocol',
    Header: 'Protocol.',
    editOptions: { inputType: 'text' },
  },
];

export const soakingColumns: {
  accessor: string;
  Header: string;
  className?: string;
  editOptions?: EditOptionsType;
}[] = [
  {
    accessor: 'ingredient',
    Header: 'Ingredient',
    editOptions: {
      inputType: 'text',
    },
  },
  {
    accessor: 'conc',
    Header: 'Conc.',
    className: 'w-2 max-w-[50px]',
    editOptions: {
      inputType: 'number',
      min: 0,
    },
  },
  {
    accessor: 'concUnit',
    Header: 'Unit',
    className: 'w-3 max-w-[70px]',
    editOptions: {
      inputType: 'select',
      options: ConcUnitOptions,
      required: true,
    },
  },
  {
    accessor: 'time',
    Header: 'Time',
    className: 'w-2 max-w-[50px]',
    editOptions: {
      inputType: 'number',
      min: 0,
    },
  },
  {
    accessor: 'timeUnit',
    Header: 'Unit.',
    className: '2-3 max-w-[70px]',
    editOptions: {
      inputType: 'select',
      options: TimeUnitOptions,
      required: true,
    },
  },
  {
    accessor: 'textInfo',
    Header: 'Text Info',
    editOptions: { inputType: 'text' },
  },
];

export const isProtectantAgentInUse = async (
  protectantAgent: CryoDTO | SoakingDTO,
  agentType: ProtectantAgentType,
): Promise<boolean> => {
  const usedAgent: (CryoDTO | SoakingDTO)[] =
    agentType === ProtectantAgentType.Cryoprotectant
      ? await CryoprotectantService.getInstance().getInUseCryoprotectantsByIds([
          protectantAgent.id,
        ])
      : await SoakingService.getInstance().getInUseSoakingsByIds([protectantAgent.id]);
  return usedAgent.length > 0;
};

export const checkProtectantAgentsUsage = async (
  agentType: ProtectantAgentType,
  protectantAgents: (CryoDTO | SoakingDTO)[],
): Promise<{ error: string; filteredAgent: (CryoDTO | SoakingDTO)[] }> => {
  const usedAgent: (CryoDTO | SoakingDTO)[] =
    agentType === ProtectantAgentType.Cryoprotectant
      ? await CryoprotectantService.getInstance().getInUseCryoprotectantsByIds(
          protectantAgents.map((item) => item.id),
        )
      : await SoakingService.getInstance().getInUseSoakingsByIds(
          protectantAgents.map((item) => item.id),
        );
  const filteredAgent: (CryoDTO | SoakingDTO)[] = protectantAgents.filter(
    (agent: CryoDTO | SoakingDTO) =>
      !usedAgent.find((_usedAgent: CryoDTO | SoakingDTO) => _usedAgent.id === agent.id),
  );

  let error = '';
  if (usedAgent.length > 0) {
    const usedAgentList = usedAgent
      .map(
        (agent, index) =>
          `${index + 1}) ${agent.ingredient}, ${agent.conc}${agent.concUnit} ${
            'pH' in agent && agent?.pH ? `,pH ${agent?.pH ?? ''}` : ''
          }${
            'time' in agent && agent?.time ? `, ${agent?.time} ${agent?.timeUnit}` : ''
          }`,
      )
      .join('\n');

    const unusedAgentList =
      filteredAgent?.length > 0
        ? `would you like to delete following unused items?\n${filteredAgent
            .map(
              (agent, index) =>
                `${index + 1}) ${agent.ingredient}, ${agent.conc}${agent.concUnit} ${
                  'pH' in agent && agent?.pH ? `, pH ${agent?.pH}` : ''
                }${
                  'time' in agent && agent?.time
                    ? `, ${agent?.time} ${agent?.timeUnit}`
                    : ''
                }`,
            )
            .join('\n')}`
        : '';

    error = `The following items are being used in a crystal and cannot be deleted:\n${usedAgentList}\n${unusedAgentList}`;
  }

  return { error, filteredAgent };
};

export const getHideModalHandler =
  (setModalDetails: React.Dispatch<React.SetStateAction<ModalDetails>>) => () => {
    setModalDetails((prev) => ({ ...prev, showModal: false }));
  };
